import { Lock } from 'lucide-react';
import {
  computeFlexEstimate,
  countBillableDates,
  daysInMonth,
  validatePlanDates,
} from '../utils/flexPlan';
import { getBlackoutLabel } from '../config/blackoutDates';
import { DEFAULT_BRAND_KEY, type BrandKey } from '../config/brands';
import { useLabels, useMonthLabel } from '../config/useLabels';

interface FlexPlanEditorProps {
  /** First-of-month (YYYY-MM-01) this plan covers. */
  planMonth: string;
  /** Per-day tuition rate (the program's tuition line item amount). */
  perDayRate: number;
  /** Currently selected dates (controlled, YYYY-MM-DD). */
  dates: string[];
  onChange?: (dates: string[]) => void;
  /** Locked / history view — calendar is read-only. */
  readOnly?: boolean;
  /** Non-tuition fees, summed, added flat to the live estimate. */
  monthlyAddOns?: number;
  /** Label for the add-ons in the estimate line (e.g. "first-month fees"). Default "fees". */
  addOnsLabel?: string;
  /** For a mid-month first month: dates before this are not billable (greyed). */
  enrollmentStartYmd?: string | null;
  /**
   * Which brand's closure calendar greys out days. Defaults to the default brand, which is
   * what every caller meant before a second brand existed — and what every Flex program is
   * today, since Flex is BHCS-only. Wrong here means offering a day the studio is shut, or
   * greying out one it is open for.
   */
  brandKey?: BrandKey;
  /**
   * The program's running weekdays (0=Sun…6=Sat), taken straight from its schedule cron.
   * These are highlighted in blue AND are the only selectable weekdays — anything the
   * program doesn't run (e.g. weekends for a weekday program, or weekdays for a weekend
   * program) is disabled. Empty/omitted → schedule unknown, so no highlight and no
   * restriction.
   */
  scheduleWeekdays?: number[];
}

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Single-month day-plan editor for flex_monthly billing. Toggling a day adds/removes it
 * from the plan; the footer shows a live "N days × $rate = $Y" estimate (floored at $0).
 * Shared by the enrollment modal (first-month preview), the admin surface, and the parent
 * surface. Locked months render read-only.
 */
export function FlexPlanEditor({
  planMonth,
  perDayRate,
  dates,
  onChange,
  readOnly = false,
  monthlyAddOns = 0,
  addOnsLabel = 'fees',
  enrollmentStartYmd = null,
  scheduleWeekdays = [],
  brandKey = DEFAULT_BRAND_KEY,
}: FlexPlanEditorProps) {
  const t = useLabels();
  const monthLabel = useMonthLabel();
  const [y, m] = planMonth.split('-').map(Number);
  const total = daysInMonth(planMonth);
  const firstDow = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  // The program's running weekdays: highlighted and the only ones selectable. Empty set
  // (schedule unknown) → no restriction.
  const schedule = new Set(scheduleWeekdays);
  const restrict = schedule.size > 0;
  const dowOf = (ymd: string) => {
    const [dy, dm, dd] = ymd.split('-').map(Number);
    return new Date(Date.UTC(dy, dm - 1, dd)).getUTCDay();
  };
  const notSelectable = (ymd: string) =>
    Boolean(getBlackoutLabel(ymd, brandKey)) || (restrict && !schedule.has(dowOf(ymd)));
  // A stale selection on a blackout or off-schedule day is dropped from the billed set.
  const selected = new Set((Array.isArray(dates) ? dates : []).filter((d) => !notSelectable(d)));

  const cells: Array<{ day: number; ymd: string; dow: number; beforeStart: boolean; blackout?: string; offSchedule?: boolean } | null> = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let day = 1; day <= total; day++) {
    const ymd = `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dow = new Date(Date.UTC(y, m - 1, day)).getUTCDay();
    const beforeStart = Boolean(enrollmentStartYmd && ymd < enrollmentStartYmd);
    cells.push({ day, ymd, dow, beforeStart, blackout: getBlackoutLabel(ymd, brandKey), offSchedule: restrict && !schedule.has(dow) });
  }

  function toggle(ymd: string) {
    if (readOnly || !onChange || notSelectable(ymd)) return;
    const next = new Set(selected);
    if (next.has(ymd)) next.delete(ymd);
    else next.add(ymd);
    onChange(validatePlanDates(planMonth, Array.from(next)).dates);
  }

  const billableDays = countBillableDates(Array.from(selected), enrollmentStartYmd);
  const estimate = computeFlexEstimate(billableDays, perDayRate, monthlyAddOns);

  // Blackout days in this month, grouped by reason (in calendar order) for the list below.
  const blackoutGroups: Array<{ label: string; days: number[] }> = [];
  for (const c of cells) {
    if (!c || !c.blackout) continue;
    const g = blackoutGroups.find((x) => x.label === c.blackout);
    if (g) g.days.push(c.day);
    else blackoutGroups.push({ label: c.blackout, days: [c.day] });
  }

  return (
    <div className="bg-white border border-gray-200 rounded-md p-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-900">
          {monthLabel(y, m - 1)}
        </h4>
        {readOnly ? (
          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
            <Lock className="h-3.5 w-3.5" /> Locked
          </span>
        ) : (
          onChange && selected.size > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-xs font-medium text-gray-500 hover:text-red-600 hover:underline"
            >
              {t('flex.clearAll')}
            </button>
          )
        )}
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {DOW.map((name, idx) => (
          <div key={name} className={`text-center text-xs font-medium py-1 ${schedule.has(idx) ? 'text-blue-600' : 'text-gray-400'}`}>
            {name}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((cell, idx) => (
          <div key={idx} className="flex items-center justify-center py-[2px]">
            {cell ? (
              <button
                type="button"
                onClick={() => toggle(cell.ymd)}
                disabled={readOnly || cell.beforeStart || !!cell.blackout || cell.offSchedule}
                aria-pressed={selected.has(cell.ymd)}
                title={cell.blackout ?? (cell.beforeStart ? 'Before enrollment start' : cell.offSchedule ? 'Not a program day' : undefined)}
                className={`w-9 h-7 rounded text-xs font-medium transition-colors ${
                  selected.has(cell.ymd)
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : cell.blackout
                    ? 'text-gray-300 line-through bg-gray-50 cursor-not-allowed'
                    : cell.offSchedule
                    ? 'text-gray-200 cursor-not-allowed'
                    : cell.beforeStart
                    ? 'text-gray-300 cursor-not-allowed'
                    : readOnly
                    ? 'text-gray-500 border border-transparent'
                    : schedule.has(cell.dow)
                    ? 'text-gray-900 hover:bg-blue-50 hover:text-blue-600 border border-blue-100'
                    : 'text-gray-700 hover:bg-gray-50 border border-gray-100'
                }`}
              >
                {cell.day}
              </button>
            ) : (
              <div className="w-9 h-7" />
            )}
          </div>
        ))}
      </div>

      <div className="mt-2 flex items-center justify-between text-sm">
        <span className="text-gray-600">
          {billableDays} day{billableDays !== 1 ? 's' : ''} × ${perDayRate}/day
          {monthlyAddOns > 0 ? ` + $${monthlyAddOns} ${addOnsLabel}` : ''}
        </span>
        <span className="font-semibold text-gray-900">${estimate.toFixed(2)}</span>
      </div>
      {billableDays === 0 && !readOnly && (
        <p className="mt-1 text-xs text-amber-600">0 days selected — this month will be paused (no invoice).</p>
      )}

      {blackoutGroups.length > 0 && (
        <div className="mt-2 border-t border-gray-100 pt-2">
          <p className="text-xs font-medium text-gray-500 mb-1">{t('flex.notAvailable')}</p>
          <ul className="text-xs text-gray-500 space-y-0.5">
            {blackoutGroups.map((g) => (
              <li key={g.label}>
                <span className="text-gray-400 line-through">{g.days.join(', ')}</span>
                <span className="text-gray-600"> — {g.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default FlexPlanEditor;
